@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginecardview

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.enginecardview.layouts.WACenterText
import org.example.project.enginecardview.layouts.WA_PLATZ_TYP
import org.example.project.enginecardview.layouts.WAPlatzCard
import org.example.project.enginecardview.layouts.WAPlatzUebersichtStyles
import org.example.project.enginekartei.KarteiApi
import org.example.project.enginetabular.TableStyles
import org.example.project.service.InfrastrukturApi
import org.example.project.service.GlobalManager
import org.example.project.service.YardApi
import org.example.project.settings.AppDropdown
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.example.project.screens_cus.infraAktiveFelder

private val WA_UEBERSICHT_FILTER_WIDE = 220.px
private val WA_UEBERSICHT_FILTER_NORMAL = 150.px

@Composable
fun CUSWAPlatzUebersicht(art: String = org.example.project.service.currentSaveArt()) {
    Style(WAPlatzUebersichtStyles)
    Style(TableStyles)

    val scope = rememberCoroutineScope()
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("yard"),
        GlobalManager.jsonDomainVersion("yard-kontrollen")
    )
    var plaetze by remember { mutableStateOf<List<All>>(emptyList()) }
    var waStandorte by remember { mutableStateOf<List<All>>(emptyList()) }
    var infra by remember { mutableStateOf<All?>(null) }
    var standortStamm by remember { mutableStateOf<List<All>>(emptyList()) }
    var datenGeladen by remember { mutableStateOf(false) }

    var selectedStandort by remember { mutableStateOf<String?>(null) }
    var selectedHalle by remember { mutableStateOf<String?>(null) }
    var selectedRegal by remember { mutableStateOf<String?>(null) }
    var selectedFach by remember { mutableStateOf<String?>(null) }
    var selectedEbene by remember { mutableStateOf<String?>(null) }
    var selectedTor by remember { mutableStateOf<String?>(null) }
    var selectedBereich by remember { mutableStateOf<String?>(null) }

    suspend fun loadGefiltertePlaetze() {
        if (selectedStandort.isNullOrBlank()) {
            plaetze = emptyList()
            waStandorte = emptyList()
            datenGeladen = true
            return
        }
        val allePlaetze = InfrastrukturApi.loadPlaetzeFiltered(
            All(
                typ = WA_PLATZ_TYP,
                standort = selectedStandort.orEmpty(),
                halle = selectedHalle.orEmpty(),
                regal = selectedRegal.orEmpty(),
                fach = selectedFach.orEmpty(),
                ebene = selectedEbene.orEmpty(),
                tor = selectedTor?.toIntOrNull(),
                bereich = selectedBereich.orEmpty()
            )
        )
        waStandorte = YardApi.loadWaStandorteFiltered(
            All(
                standort = selectedStandort.orEmpty(),
                tor = selectedTor?.toIntOrNull(),
                columns = allePlaetze.map { it.platzcode }.filter { it.isNotBlank() }
            )
        )
        plaetze = allePlaetze
        datenGeladen = true
    }

    LaunchedEffect(art, refreshVersion) {
        infra = InfrastrukturApi.loadInfra()
        standortStamm = KarteiApi.loadStandorte().sortedBy { it.id.toIntOrNull() ?: 0 }
        if (datenGeladen && !selectedStandort.isNullOrBlank()) {
            loadGefiltertePlaetze()
        }
    }

    val standorte = remember(standortStamm) {
        standortStamm.mapNotNull { it.id.takeIf(String::isNotBlank) }.distinct().sortedWith(compareBy<String> { it.toIntOrNull() ?: Int.MAX_VALUE }.thenBy { it })
    }
    val selectedStandortData = standortStamm.firstOrNull { it.id == selectedStandort }
    val aktiveFelder = infraAktiveFelder(infra, WA_PLATZ_TYP).toSet()
    fun range(von: String, bis: String, pad: Int): List<String> {
        val start = von.toIntOrNull() ?: return emptyList()
        val end = bis.toIntOrNull() ?: start
        return (start..end).map { it.toString().padStart(pad, '0') }
    }
    val hallen = remember(selectedStandortData) { range(selectedStandortData?.halleVon.orEmpty(), selectedStandortData?.halleBis.orEmpty(), 2) }
    val regale = remember(selectedStandortData) { range(selectedStandortData?.regalVon.orEmpty(), selectedStandortData?.regalBis.orEmpty(), 2) }
    val faecher = remember(selectedStandortData) { range(selectedStandortData?.fachVon.orEmpty(), selectedStandortData?.fachBis.orEmpty(), 3) }
    val ebenen = remember(selectedStandortData) { range(selectedStandortData?.ebeneVon.orEmpty(), selectedStandortData?.ebeneBis.orEmpty(), 2) }
    val tore = remember(selectedStandortData) { range(selectedStandortData?.toreVon.orEmpty(), selectedStandortData?.toreBis.orEmpty(), 3) }
    val bereiche = remember(plaetze) { plaetze.mapNotNull { it.bereich.takeIf(String::isNotBlank) }.distinct().sorted() }

    fun passtZuAktivenFiltern(platz: All): Boolean =
        (selectedStandort == null || platz.standort == selectedStandort) &&
            (selectedHalle == null || platz.halle == selectedHalle) &&
            (selectedRegal == null || platz.regal == selectedRegal) &&
            (selectedFach == null || platz.fach == selectedFach) &&
            (selectedEbene == null || platz.ebene == selectedEbene) &&
            (selectedTor == null || platz.tor?.toString().orEmpty().padStart(3, '0') == selectedTor || platz.tor?.toString().orEmpty() == selectedTor) &&
            (selectedBereich == null || platz.bereich == selectedBereich)

    val aktivePlaetze = plaetze.filter { passtZuAktivenFiltern(it) }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            width(100.percent)
            height(100.percent)
        }
    }) {
        Div({
            style {
                flexShrink(0)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                flexWrap(FlexWrap.Wrap)
                gap(10.px)
                padding(12.px, 16.px)
            }
        }) {
            Dropdown("Standort", standorte, WA_UEBERSICHT_FILTER_WIDE) {
                selectedStandort = it
                selectedHalle = null
                selectedRegal = null
                selectedFach = null
                selectedEbene = null
                selectedTor = null
                selectedBereich = null
            }
            if ("halle" in aktiveFelder) Dropdown("Halle", hallen, WA_UEBERSICHT_FILTER_NORMAL) { selectedHalle = it }
            if ("regal" in aktiveFelder) Dropdown("Regal", regale, WA_UEBERSICHT_FILTER_NORMAL) { selectedRegal = it }
            if ("fach" in aktiveFelder) Dropdown("Fach", faecher, WA_UEBERSICHT_FILTER_NORMAL) { selectedFach = it }
            if ("ebene" in aktiveFelder) Dropdown("Ebene", ebenen, WA_UEBERSICHT_FILTER_NORMAL) { selectedEbene = it }
            if ("tor" in aktiveFelder) Dropdown("Tor", tore, WA_UEBERSICHT_FILTER_NORMAL) { selectedTor = it }
            if ("bereich" in aktiveFelder) Dropdown("Bereich", bereiche, WA_UEBERSICHT_FILTER_NORMAL) { selectedBereich = it }
            CardViewActionButton("Suchen", "images/suche.svg") {
                scope.launch { loadGefiltertePlaetze() }
            }
            CardViewActionButton("Reset", "images/refresh.svg") {
                selectedStandort = null
                selectedHalle = null
                selectedRegal = null
                selectedFach = null
                selectedEbene = null
                selectedTor = null
                selectedBereich = null
                plaetze = emptyList()
                waStandorte = emptyList()
                datenGeladen = false
            }
        }

        when {
            selectedStandort == null -> WACenterText("Bitte Standort auswählen und Laden drücken.")
            !datenGeladen -> WACenterText("Filter setzen und Laden drücken.")
            aktivePlaetze.isEmpty() -> WACenterText("Keine passenden WA-Plätze gefunden.")
            else -> Div({
                style {
                    flex(1)
                    overflowY("auto")
                    padding(24.px, 40.px)
                    display(DisplayStyle.Grid)
                    gridTemplateColumns("repeat(auto-fill, minmax(360px, 360px))")
                    justifyContent(JustifyContent.Center)
                    columnGap(18.px)
                    rowGap(18.px)
                }
            }) {
                aktivePlaetze.sortedBy { it.platzcode }.forEach { platz ->
                    val wa = waStandorte.find { it.platzcode == platz.platzcode && it.zustand < 60 }
                    WAPlatzCard(platz, wa)
                }
            }
        }
    }
}

@Composable
private fun Dropdown(label: String, options: List<String>, width: CSSSizeValue<CSSUnit.px>, onChange: (String?) -> Unit) {
    var selectedValue by remember(label, options) { mutableStateOf("") }
    Div({ style { this.width(width) } }) {
        AppDropdown(
            options = buildList {
                add(All(key = "", label = label))
                addAll(options.map { All(key = it, label = it) })
            },
            selectedValue = selectedValue,
            onChange = {
                selectedValue = it
                onChange(it.ifBlank { null })
            },
            attrs = { classes(TableStyles.button) }
        )
    }
    CardViewDivider()
}
